import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Skeleton } from 'antd';
import styled from 'styled-components';

import { UseOrbitSessionsResult } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import OrbitThemeScope from '../../OrbitThemeScope';
import { TOrbit } from '../../i18n/TModules';
import { orbitTokens } from '../../styles';
import { OrbitSession } from '../../types';
import OrbitChatRowIcon from './OrbitChatRowIcon';
import { PlainButton, type } from './pageStyles';

/** Figma 6454:8168: 267 x 406. */
const FLYOUT_WIDTH = 267;
const FLYOUT_HEIGHT = 406;
const VIEWPORT_GAP = 8;
/** The rows' pitch (≈31); one row from the end is close enough to ask for the next page. */
const ROW_PITCH = 31;
/** 6454:8092: the chat glyph sits 11 in; the loader and the empty line take the same inset. */
export const FLYOUT_ROW_INSET = 11;

/*
  Portalled to the body like the rail's other menus, so the rail's scroller neither clips it nor
  holds its loader, with literal colours for the same reason. Fixed at the row's right edge and top
  (set inline from the row's box), over the page. #15203E with radii 0/18/18/18: its square
  top-left corner meets the row's band, so the two read as one shape.
*/
const Card = styled.div`
  position: fixed;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  width: ${FLYOUT_WIDTH}px;
  height: ${FLYOUT_HEIGHT}px;
  padding: 9px 4px 8px 0;
  border-radius: 0 18px 18px 18px;
  background: #15203e;
  font-family: ${orbitTokens.fontFamily};
`;

// 6473:3225: a 5 wide bar, #0D1428 track and #4F46E5 thumb at r100.
const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-color: #4f46e5 #0d1428;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 100px;
    background: #0d1428;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background: #4f46e5;
  }
`;

// Rows at a ≈31 pitch: the 14 glyph at x 11 in #64709A, the label at x 34, Regular 12 #AEBBD0.
const Row = styled(PlainButton)`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${ROW_PITCH}px;
  padding: 0 ${FLYOUT_ROW_INSET}px;
  color: #aebbd0;
  text-align: left;
  ${type('helping')}

  &:hover,
  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.06);
  }

  > :first-child {
    flex: none;
    margin-right: 9px;
    color: #64709a;
  }

  > :last-child {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// antd draws each loading bar 16 tall; 15 between them keeps the rows' pitch.
const Loading = styled.div`
  padding: 7px ${FLYOUT_ROW_INSET}px;

  && .ant-skeleton-paragraph > li + li {
    margin-top: ${ROW_PITCH - 16}px;
  }
`;

const Note = styled.p`
  margin: 4px ${FLYOUT_ROW_INSET}px;
  color: #64709a;
  ${type('helping')}
`;

/** At the row's right edge, over the rail's 1px rule, and level with the row's top. */
const placeBeside = (anchor: HTMLElement) => {
  const rect = anchor.getBoundingClientRect();
  return { top: rect.top, left: rect.right - 1 };
};

export interface IOrbitWorkspaceFlyoutProps {
  /** The Workspace row it hangs from. */
  anchor: HTMLElement;
  label: string;
  chats: UseOrbitSessionsResult;
  onSelectChat: (session: OrbitSession) => void;
  onClose: () => void;
}

/** A Workspace's chats, latest activity first, drawn out of its rail row over the page. */
const OrbitWorkspaceFlyout: React.FC<IOrbitWorkspaceFlyoutProps> = ({
  anchor,
  label,
  chats,
  onSelectChat,
  onClose
}) => {
  const { t } = useMultiLanguageTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() => placeBeside(anchor));

  useLayoutEffect(() => {
    const place = () => setPosition(placeBeside(anchor));
    place();
    window.addEventListener('resize', place);
    // Captured: the rail's own scroller moves the row.
    document.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
    };
  }, [anchor]);

  // Escape returns focus to the row; a press outside the card and its row closes it. The row's
  // own click toggles, so it is left to that.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        anchor.focus();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!cardRef.current?.contains(target) && !anchor.contains(target)) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [anchor, onClose]);

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (
      chats.hasMore &&
      !chats.isLoadingMore &&
      list.scrollTop + list.clientHeight >= list.scrollHeight - ROW_PITCH
    ) {
      chats.loadMore();
    }
  };

  return createPortal(
    // Portalled to `document.body`, so it re-declares the tokens the card reads.
    <OrbitThemeScope>
      <Card
        ref={cardRef}
        id={anchor.getAttribute('aria-controls') ?? undefined}
        role="dialog"
        aria-label={label}
        style={{
          top: position.top,
          left: position.left,
          maxHeight: `calc(100vh - ${position.top + VIEWPORT_GAP}px)`
        }}
        data-testid="orbit-page-workspace-flyout"
      >
        <List onScroll={onScroll} data-testid="orbit-page-workspace-flyout-list">
          {chats.sessions.map(session => (
            <Row
              key={session.session_id}
              type="button"
              data-testid="orbit-page-workspace-flyout-row"
              onClick={() => onSelectChat(session)}
            >
              <OrbitChatRowIcon />
              <span>{session.title}</span>
            </Row>
          ))}
          {chats.isLoading || chats.isLoadingMore ? (
            <Loading role="status" data-testid="orbit-page-workspace-flyout-loading">
              <Skeleton active title={false} paragraph={{ rows: 3, width: '100%' }} />
            </Loading>
          ) : null}
          {chats.hasLoaded && chats.sessions.length === 0 && !chats.error ? (
            <Note>{t(`${TOrbit}.page.workspaceFlyout.empty`)}</Note>
          ) : null}
          {chats.error ? (
            <Note role="alert">{t(`${TOrbit}.page.workspaceFlyout.failed`)}</Note>
          ) : null}
        </List>
      </Card>
    </OrbitThemeScope>,
    document.body
  );
};

export default OrbitWorkspaceFlyout;
